/**
 * Geometria della griglia delle ore di Syncfusion, per ancorare lo scroll a
 * un'ora invece che ai pixel: con la riga Ore del riquadro Visualizzazione
 * (Tutte | Prenotabili) le ore mostrate cambiano, e gli stessi pixel
 * indicherebbero un'altra ora. Vale per tutti i calendari della console
 * (useSchedulerPersistence), con le ore in verticale
 * (Giorno, 3 Giorni, Lavorativo, Settimana) o in orizzontale (TimelineDay, la
 * vista Orizzontale dei giornalieri Studio e Utente).
 */

// Le parti di Syncfusion usate qui, non tutte nei tipi pubblici
export interface ScheduleInterno {
  element?: HTMLElement;
  isDestroyed?: boolean;
  /** iPad e telefoni: nelle viste verticali scorre un contenitore unico di ore e celle */
  isAdaptive?: boolean;
  currentView?: string;
  activeViewOptions?: {
    startHour?: string;
    endHour?: string;
    timeScale?: { enable?: boolean };
  } | null;
  uiStateValues?: { top: number; left?: number } | null;
  /** Pixel salvati (enablePersistence) e rimessi dopo ogni ridisegno */
  scrollTop?: number;
  scrollLeft?: number;
}

/**
 * Direzione delle ore: dall'alto in basso (viste verticali) o da sinistra a
 * destra (TimelineDay).
 */
export type AsseOre = 'verticale' | 'orizzontale';

/** Griglia delle ore disegnata adesso. */
export interface GrigliaOre {
  asse: AsseOre;
  /** Elemento che scorre, lo stesso di getScrollableElement di Syncfusion */
  area: HTMLElement;
  /** Prima ora e fine della griglia */
  inizio: number;
  fine: number;
  /**
   * Pixel di un'ora lungo l'asse delle ore (altezza o larghezza): dipendono
   * dallo zoom, non dalle ore mostrate
   */
  pixelOra: number;
}

// currentView di Syncfusion: 3 Giorni è un Day, il Lavorativo una Week. Le
// timeline di più giorni non ci sono nella console: un'ora a sinistra non
// direbbe di quale giorno
const ASSE_DELLA_VISTA = new Map<string, AsseOre>([
  ['Day', 'verticale'],
  ['Week', 'verticale'],
  ['WorkWeek', 'verticale'],
  ['TimelineDay', 'orizzontale'],
]);

// 'HH:mm' in ore; null se non è un orario
const getOre = (orario?: string): number | null => {
  if (!orario) return null;
  const [ore, minuti = '0'] = orario.split(':');
  const valore = Number(ore) + Number(minuti) / 60;
  return Number.isFinite(valore) ? valore : null;
};

// Pixel scorsi lungo l'asse delle ore
const getScroll = ({ asse, area }: GrigliaOre): number =>
  asse === 'verticale' ? area.scrollTop : area.scrollLeft;

/**
 * Griglia delle ore così come Syncfusion l'ha disegnata: ore da activeViewOptions
 * e misura dalle celle del DOM, aggiornati insieme a ogni ridisegno. null fuori
 * dalle viste a griglia oraria (Mese, Agenda) o senza celle.
 */
export const getGrigliaOre = (
  schedule: ScheduleInterno | null | undefined
): GrigliaOre | null => {
  if (!schedule?.element || schedule.isDestroyed) return null;
  const opzioni = schedule.activeViewOptions;
  const asse = ASSE_DELLA_VISTA.get(schedule.currentView ?? '');
  if (!opzioni?.timeScale?.enable || !asse) return null;
  const inizio = getOre(opzioni.startHour);
  const fine = getOre(opzioni.endHour);
  // Nella vista orizzontale scorre sempre l'area delle celle, anche sull'iPad
  const area = schedule.element.querySelector<HTMLElement>(
    asse === 'verticale' && schedule.isAdaptive
      ? '.e-scroll-container'
      : '.e-content-wrap'
  );
  // Le celle: righe delle ore in verticale, colonne delle ore in orizzontale
  const celle = schedule.element.querySelector<HTMLElement>(
    '.e-content-wrap .e-content-table > tbody'
  );
  if (!area || !celle || inizio === null || fine === null || fine <= inizio)
    return null;
  const misura = asse === 'verticale' ? celle.offsetHeight : celle.offsetWidth;
  const pixelOra = misura / (fine - inizio);
  return pixelOra > 0 ? { asse, area, inizio, fine, pixelOra } : null;
};

/**
 * Prima ora visibile della griglia, quella in cima o a sinistra (es. 8.5 = le
 * 8:30); null fuori dalle viste orarie.
 */
export const leggiPrimaOraVisibile = (
  schedule: ScheduleInterno | null | undefined
): number | null => {
  const griglia = getGrigliaOre(schedule);
  return griglia === null
    ? null
    : griglia.inizio + getScroll(griglia) / griglia.pixelOra;
};

/**
 * Pixel che Syncfusion rimette sull'asse delle ore dopo ogni ridisegno
 * (enablePersistence: al montaggio sono quelli salvati). undefined se non ce
 * ne sono (primo avvio) o se non li rimette: sull'iPad le viste verticali
 * scorrono un altro contenitore.
 */
export const leggiPixelRipristinati = (
  schedule: ScheduleInterno | null | undefined,
  asse: AsseOre
): number | undefined => {
  if (!schedule) return undefined;
  if (asse === 'orizzontale') return schedule.scrollLeft;
  return schedule.isAdaptive ? undefined : schedule.scrollTop;
};

/**
 * Porta all'inizio della griglia (in cima, o a sinistra) l'ora indicata, tenuta
 * dentro le ore mostrate (il browser limita anche lo scroll alla fine). Scrive
 * i pixel anche dove Syncfusion li riprende dopo ogni ridisegno (scrollUiUpdate,
 * poi retainScrollPosition): senza, l'ora salterebbe al ridisegno successivo,
 * per esempio all'arrivo degli appuntamenti. Restituisce l'ora rimasta davvero
 * all'inizio; null fuori dalle viste orarie.
 */
export const portaPrimaOraVisibile = (
  schedule: ScheduleInterno | null | undefined,
  ora: number
): number | null => {
  const griglia = getGrigliaOre(schedule);
  if (griglia === null || !schedule) return null;
  const { asse, area, inizio, fine, pixelOra } = griglia;
  const pixel = Math.round(
    (Math.min(Math.max(ora, inizio), fine) - inizio) * pixelOra
  );
  if (asse === 'orizzontale') {
    area.scrollLeft = pixel;
    const scrollLeft = area.scrollLeft;
    if (schedule.uiStateValues) schedule.uiStateValues.left = scrollLeft;
    // Le date e le ore in testa scorrono a parte: le allinea subito, anche se
    // lo scroll delle celle non cambia e non manda nessun evento
    const intestazione = schedule.element?.querySelector<HTMLElement>(
      '.e-date-header-wrap'
    );
    if (intestazione) intestazione.scrollLeft = scrollLeft;
    schedule.scrollLeft = scrollLeft;
    return inizio + scrollLeft / pixelOra;
  }
  area.scrollTop = pixel;
  const scrollTop = area.scrollTop;
  if (schedule.uiStateValues) schedule.uiStateValues.top = scrollTop;
  if (!schedule.isAdaptive) {
    // Fuori dall'iPad le ore a sinistra scorrono a parte: le allinea subito,
    // anche se lo scroll delle celle non cambia e non manda nessun evento
    const ore =
      schedule.element?.querySelector<HTMLElement>('.e-time-cells-wrap');
    if (ore) ore.scrollTop = scrollTop;
    schedule.scrollTop = scrollTop;
  }
  return inizio + scrollTop / pixelOra;
};
